import React, { useEffect, useState } from 'react';
import { Button, Input, Typography, theme } from 'antd';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconMapPin, IconSend } from '@tabler/icons-react';
import { message } from '../../services/toastMessage';
import BackButton from '../../components/BackButton';
import DictateButton from '../../components/DictateButton';
import GearLoadingState from '../../components/GearLoadingState';
import JobClarifier from '../../components/quotes/JobClarifier';
import JobPhotos from '../../components/quotes/JobPhotos';
import LocationAutocomplete from '../../components/LocationAutocomplete';
import StaticMap from '../../components/StaticMap';
import { useSpeechDictation } from '../../hooks/useSpeechDictation';
import { useAppBreakpoint } from '../../hooks/useAppBreakpoint';
import { getPublicService } from '../../services/ServiceService';
import { createServiceRequest } from '../../services/ServiceRequestService';
import { IServiceDetail } from '../../interfaces/heavyDutyMachinery/IService';
import { ILocation } from '../../interfaces/heavyDutyMachinery/ILocation';
import {
  IServiceRequestClarification,
  IServiceRequestPhoto,
  SERVICE_REQUEST_URGENCIES,
  ServiceRequestUrgency,
} from '../../interfaces/heavyDutyMachinery/IServiceRequest';
import { SERVICE_REQUEST_URGENCY_LABEL_KEYS } from '../../constants/serviceRequestOptions';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import { formatCurrency } from '../../helpers/currencyHelper';
import { TEAL, TEAL_SOFT_BG, SURFACE_1_SOFT, CORAL, CORAL_SOFT_BG } from '../../theme/creamyTokens';

const { Text, Title, Paragraph } = Typography;
const { TextArea } = Input;

/** Same floor the backend refuses below -- checked here so three words never cost a round trip. */
const MIN_DESCRIPTION_LENGTH = 15;

/**
 * "Cuéntame qué necesitas."
 *
 * The screen this whole feature exists for, and the one thing to understand
 * about it is what is NOT on it: **no calendar, no time picker, no duration.**
 * Somebody with a leak does not know whether it is two hours or all morning.
 * The plumber does, and not until they ask or look. Asking the customer to
 * pick a three-hour window is asking them to do the professional's job, and
 * they will get it wrong every time.
 *
 * What they get instead is a box to talk into. The microphone is not a
 * novelty here: half of these are typed one-handed, standing in front of the
 * problem.
 */
const RequestQuote: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { isMobile } = useAppBreakpoint();
  const { token } = theme.useToken();
  const dictation = useSpeechDictation(i18n.language);

  const [service, setService] = useState<IServiceDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [description, setDescription] = useState('');
  const [urgency, setUrgency] = useState<ServiceRequestUrgency>('Flexible');
  const [city, setCity] = useState('');
  /**
   * The place they picked from the dropdown, which is the only way
   * coordinates ever appear.
   *
   * Kept apart from `city` on purpose: `city` is whatever is in the box,
   * including text somebody typed and never resolved, and a request whose
   * words say one town while its pin sits in another is worse than one with
   * no pin at all -- the professional would price the drive against the pin.
   */
  const [picked, setPicked] = useState<ILocation>();
  const [address, setAddress] = useState('');
  const [sending, setSending] = useState(false);
  // What the assistant asked and what they answered, plus its short version.
  // Both optional all the way through: a request sent without touching any of
  // it is exactly the request that would have been sent before.
  const [clarifications, setClarifications] = useState<IServiceRequestClarification[]>([]);
  const [assistantSummary, setAssistantSummary] = useState<string>();
  const [photos, setPhotos] = useState<IServiceRequestPhoto[]>([]);

  // Whether asking this professional costs anything, and how much it reads as.
  // Both derived rather than stored: the listing is the only source, and a
  // request sent without one (naming a provider directly) has no fee to show
  // because there is no listing to have shown it on first.
  const chargesVisitFee = !!service?.providerChargesVisitFee && (service?.providerVisitFeeAmount ?? 0) > 0;
  const visitFeeLabel = chargesVisitFee
    ? formatCurrency(service!.providerVisitFeeAmount!, service!.hostCurrencyCode, i18n.language)
    : '';

  // With a listing the server works out whose it is -- the public detail
  // deliberately does not expose the professional's profile id, and it does
  // not need to. Only the "asked directly, not through an advert" case has to
  // name them, and that comes from wherever the link was built.
  const providerProfileId = searchParams.get('providerId') ?? undefined;

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }
    getPublicService(id)
      .then(setService)
      .catch(() => message.error(translate(t, TRANSLATIONS.SERVICE_REQUEST_LOAD_ERROR)))
      .finally(() => setLoading(false));
  }, [id, t]);

  // Dictation lands in the field, where it can be read and fixed before it
  // goes anywhere. A recogniser mishears, and finding out by reading the
  // quote you got back is too late.
  useEffect(() => {
    if (dictation.transcript) setDescription(dictation.transcript);
  }, [dictation.transcript]);

  useEffect(() => {
    if (dictation.error) message.error(translate(t, TRANSLATIONS.DICTATE_ERROR));
  }, [dictation.error, t]);

  /**
   * The pin, but only while the box still says what the pin said.
   *
   * Derived rather than cleared on every keystroke, because clearing would
   * depend on the order LocationAutocomplete happens to fire onChange and
   * onSelectLocation in -- it fires both on a pick, and a component that
   * swapped them would silently start sending requests with no coordinates.
   * Comparing instead is true whatever the order: type something else, or
   * press clear, and the pin stops counting. It fails towards no
   * coordinates, which is the safe direction.
   */
  const pin = picked && [picked.locality, picked.province, picked.region, picked.formattedAddress]
    .filter(Boolean)
    .includes(city.trim())
    ? picked
    : undefined;

  const handleSend = async () => {
    const job = description.trim();
    if (job.length < MIN_DESCRIPTION_LENGTH) {
      message.error(translate(t, TRANSLATIONS.SERVICE_REQUEST_DESCRIPTION_TOO_SHORT));
      return;
    }
    if (!service?.id && !providerProfileId) {
      message.error(translate(t, TRANSLATIONS.SERVICE_REQUEST_LOAD_ERROR));
      return;
    }

    dictation.stop();
    setSending(true);
    try {
      const created = await createServiceRequest({
        serviceProviderProfileId: providerProfileId,
        serviceId: service?.id,
        description: job,
        urgency,
        city: city.trim() || undefined,
        province: pin?.province || undefined,
        // Whatever they typed wins over what the map returned: somebody who
        // writes "nave 12" after picking the industrial estate knows
        // something Azure does not.
        formattedAddress: address.trim() || pin?.formattedAddress || undefined,
        latitude: pin?.latitude,
        longitude: pin?.longitude,
        clarifications: clarifications.length > 0 ? clarifications : undefined,
        assistantSummary,
        photoIds: photos.length > 0 ? photos.map((p) => Number(p.id)) : undefined,
      });
      message.success(translate(t, TRANSLATIONS.SERVICE_REQUEST_SENT));
      navigate(`/heavy-duty-machinery/service-requests/${created.id}`, { replace: true });
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.SERVICE_REQUEST_SEND_ERROR));
    } finally {
      setSending(false);
    }
  };

  if (loading) return <GearLoadingState />;

  return (
    <div style={{ minHeight: '100vh', background: SURFACE_1_SOFT, paddingBottom: 40 }}>
      <div style={{ maxWidth: 640, margin: '0 auto', padding: isMobile ? '12px 16px' : '24px 20px' }}>
        <BackButton fallbackPath="/heavy-duty-machinery/services" />

        <Title level={3} style={{ marginTop: 12, marginBottom: 4 }}>
          {translate(t, TRANSLATIONS.SERVICE_REQUEST_TITLE)}
        </Title>
        <Paragraph type="secondary" style={{ marginBottom: 20 }}>
          {service?.title
            ? translate(t, TRANSLATIONS.SERVICE_REQUEST_SUBTITLE_WITH_SERVICE, { service: service.title })
            : translate(t, TRANSLATIONS.SERVICE_REQUEST_SUBTITLE)}
        </Paragraph>

        <div
          style={{
            background: token.colorBgContainer,
            borderRadius: 20,
            padding: isMobile ? 18 : 24,
            boxShadow: '0 1px 2px rgba(22,33,28,.05), 0 10px 30px rgba(22,33,28,.06)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <Text strong style={{ fontSize: 14 }}>
              {translate(t, TRANSLATIONS.SERVICE_REQUEST_DESCRIPTION_LABEL)}
            </Text>
            <span style={{ marginLeft: 'auto' }}>
              <DictateButton
                supported={dictation.supported}
                listening={dictation.listening}
                onStart={() => dictation.start(description)}
                onStop={dictation.stop}
                disabled={sending}
              />
            </span>
          </div>

          <TextArea
            rows={6}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={translate(
              t,
              dictation.listening
                ? TRANSLATIONS.SERVICE_REQUEST_LISTENING
                : TRANSLATIONS.SERVICE_REQUEST_DESCRIPTION_PLACEHOLDER
            )}
            maxLength={4000}
            style={{ borderRadius: 14, marginBottom: 20 }}
          />

          {/* Between the description and everything else, because it is about
              the description -- and after it, because there is nothing to ask
              about an empty box. */}
          <JobClarifier
            description={description}
            trade={service?.serviceCategoryName}
            serviceId={service?.id}
            onChange={(pairs, summary) => {
              setClarifications(pairs);
              setAssistantSummary(summary);
            }}
          />

          {/* Straight after the words, because it is the same act: showing
              somebody the problem. */}
          <JobPhotos photos={photos} onChange={setPhotos} />

          <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 8 }}>
            {translate(t, TRANSLATIONS.SERVICE_REQUEST_URGENCY_LABEL)}
          </Text>
          {/* Three pills, not a dropdown. It is one tap, and it is what lets a
              professional triage ten of these on a phone at seven in the
              evening -- which is when they actually read them. */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
            {SERVICE_REQUEST_URGENCIES.map((value) => {
              const selected = urgency === value;
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => setUrgency(value)}
                  style={{
                    border: `1px solid ${selected ? TEAL : token.colorBorder}`,
                    background: selected ? TEAL_SOFT_BG : 'transparent',
                    color: selected ? TEAL : token.colorText,
                    borderRadius: 999,
                    padding: '9px 16px',
                    fontSize: 14,
                    cursor: 'pointer',
                    transition: 'background-color .15s ease, border-color .15s ease',
                  }}
                >
                  {translate(t, SERVICE_REQUEST_URGENCY_LABEL_KEYS[value])}
                </button>
              );
            })}
          </div>

          <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 8 }}>
            {translate(t, TRANSLATIONS.SERVICE_REQUEST_WHERE_LABEL)}
          </Text>
          {/* Asked because travel is a real line on a trade quote, and a price
              worked out without knowing where the job is will be wrong by
              exactly the drive.

              The same Azure Maps picker every other address in this app uses
              (see the publish wizard's own city field) rather than a box to
              type into: picking a suggestion is what stores the coordinates,
              and the coordinates are what let the professional see the job on
              a map instead of guessing from a town name. */}
          <div style={{ marginBottom: 10 }}>
            <LocationAutocomplete
              value={city}
              // The only writer of `city`: LocationAutocomplete commits the
              // clean place name through here on a pick as well as on
              // typing, so nothing else needs to set it.
              onChange={setCity}
              onSelectLocation={setPicked}
              includeCountryInLabel
              allowClear
              disabled={sending}
              placeholder={translate(t, TRANSLATIONS.SERVICE_REQUEST_CITY_PLACEHOLDER)}
            />
          </div>
          <Input
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder={translate(t, TRANSLATIONS.SERVICE_REQUEST_ADDRESS_PLACEHOLDER)}
            maxLength={500}
            prefix={<IconMapPin size={16} style={{ color: token.colorTextTertiary }} />}
            style={{ borderRadius: 999, height: 46, marginBottom: pin ? 12 : 20 }}
          />

          {/* Shown the moment there is a pin, because the only way somebody
              catches having picked the wrong "Burgos" is by seeing it. */}
          {pin && (
            <div style={{ marginBottom: 20 }}>
              <StaticMap latitude={pin.latitude} longitude={pin.longitude} height={160} />
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
                {pin.formattedAddress}
              </Text>
            </div>
          )}

          <Button
            type="primary"
            block
            size="large"
            icon={<IconSend size={16} />}
            loading={sending}
            onClick={handleSend}
            style={{ borderRadius: 999, height: 50, fontSize: 16 }}
          >
            {translate(t, TRANSLATIONS.SERVICE_REQUEST_SEND)}
          </Button>

          {/* The one place this screen cannot afford to be vague, and the
              reason the two branches are exclusive: "no pagas nada todavía" is
              the truth for almost every professional and a lie for one who
              charges for the journey. Whichever of the two is true is the only
              one shown, and it is shown HERE -- next to the button, at the
              moment they decide -- rather than somewhere they may have scrolled
              past. They were already told on the card; this is the second time,
              on purpose. */}
          {chargesVisitFee ? (
            <div
              style={{
                marginTop: 12,
                padding: '10px 14px',
                borderRadius: 14,
                background: CORAL_SOFT_BG,
                color: CORAL,
                fontSize: 13,
                textAlign: 'center',
              }}
            >
              {translate(t, TRANSLATIONS.SERVICE_VISIT_FEE_NOTICE, { amount: visitFeeLabel })}
            </div>
          ) : (
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 12, textAlign: 'center' }}>
              {translate(t, TRANSLATIONS.SERVICE_REQUEST_NO_COMMITMENT)}
            </Text>
          )}
        </div>
      </div>
    </div>
  );
};

export default RequestQuote;
